import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS,
    FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS,
} from 'src/features/financialDocuments/utils/constants';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS,
    FINANCIAL_DOCUMENT_STATUS,
    FINANCIAL_DOCUMENT_TYPE,
} from 'src/features/financialDocuments/utils/types';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { SimulationFinancialDocument } from 'src/features/billingSimulation/utils/types';
import BillingSimulationFinancialDocumentRowsTable from 'src/features/billingSimulation/components/tables/BillingSimulationFinancialDocumentRowsTable';

type BillingSimulationFinancialDocumentsTableProps = {
    documents: SimulationFinancialDocument[];
    isLoading?: boolean;
};

const SKELETON_BLOCK_COUNT = 2;

const BillingSimulationFinancialDocumentsTable = ({
    documents,
    isLoading,
}: BillingSimulationFinancialDocumentsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 gap-6">
                {Array.from({ length: SKELETON_BLOCK_COUNT }).map((_, i) => (
                    <Skeleton key={i} className="h-72 w-full" />
                ))}
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-gray-500">
                {formatMessage(billingSimulationMessages.noFinancialDocuments)}
            </div>
        );
    }

    return (
        <div className="divide-y divide-gray-200">
            {documents.map((doc) => {
                const docType = doc.type as FINANCIAL_DOCUMENT_TYPE;
                const docStatus = doc.status as FINANCIAL_DOCUMENT_STATUS;
                const docPaymentStatus = doc.payment_status as FINANCIAL_DOCUMENT_PAYMENT_STATUS;
                const typeBadge = FINANCIAL_DOCUMENT_TYPE_BADGE_OPTIONS[docType];
                const statusBadge = FINANCIAL_DOCUMENT_STATUS_BADGE_OPTIONS[docStatus];
                const paymentStatusBadge = FINANCIAL_DOCUMENT_PAYMENT_STATUS_BADGE_OPTIONS[docPaymentStatus];

                const sectionTitle = doc.number ?? formatMessage(financialDocumentsMessages.draft);

                const dashboardBlockData = {
                    section1: [
                        {
                            title: paymentsMessages.type,
                            value: typeBadge ? (
                                <Badge theme={typeBadge.theme || 'gray'}>
                                    <FontAwesomeIcon icon={typeBadge.icon as IconProp} />
                                    {formatMessage(typeBadge.label)}
                                </Badge>
                            ) : (
                                '-'
                            ),
                        },
                        {
                            title: generalMessages.status,
                            value: statusBadge ? (
                                <Badge theme={statusBadge.theme || 'gray'}>
                                    <FontAwesomeIcon icon={statusBadge.icon as IconProp} />
                                    {formatMessage(statusBadge.label)}
                                </Badge>
                            ) : (
                                '-'
                            ),
                        },
                        {
                            title: paymentsMessages.paymentStatus,
                            value: paymentStatusBadge ? (
                                <Badge theme={paymentStatusBadge.theme || 'gray'}>
                                    <FontAwesomeIcon icon={paymentStatusBadge.icon as IconProp} />
                                    {formatMessage(paymentStatusBadge.label)}
                                </Badge>
                            ) : (
                                '-'
                            ),
                        },
                    ],
                    section2: [
                        {
                            title: financialDocumentsMessages.issueDate,
                            value: getFormattedDateTime(doc.issue_due_date ?? undefined)?.formattedDate ?? '-',
                        },
                        {
                            title: financialDocumentsMessages.paymentDueDate,
                            value: getFormattedDateTime(doc.payment_due_date ?? undefined)?.formattedDate ?? '-',
                        },
                        {
                            title: financialDocumentsMessages.taxEventDate,
                            value: getFormattedDateTime(doc.tax_event_date ?? undefined)?.formattedDate ?? '-',
                        },
                    ],
                    section3: [
                        {
                            title: commonMessages.currency,
                            value: <CellCurrencyBadge currency={doc.currency} />,
                        },
                        {
                            title: paymentsMessages.amount,
                            value: formatNumber(doc.amount / 100, { style: 'currency', currency: doc.currency }),
                        },
                        {
                            title: financialDocumentsMessages.taxAmount,
                            value: formatNumber(doc.tax_amount / 100, { style: 'currency', currency: doc.currency }),
                        },
                        {
                            title: financialDocumentsMessages.amountWithTax,
                            value: formatNumber(doc.amount_with_tax / 100, {
                                style: 'currency',
                                currency: doc.currency,
                            }),
                        },
                    ],
                };

                return (
                    <div key={doc.id} className="flex flex-col gap-4 py-8 first:pt-0 last:pb-0 sm:gap-6">
                        <SectionBlockTitle className="text-xl whitespace-nowrap">{sectionTitle}</SectionBlockTitle>
                        <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={2} />
                        <BillingSimulationFinancialDocumentRowsTable rows={doc.rows} currency={doc.currency} />
                    </div>
                );
            })}
        </div>
    );
};

export default BillingSimulationFinancialDocumentsTable;
